import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { apiGet } from '../utils/api';
import {
  getHolidayByDate as lookupHolidayByDate,
  setHolidaysCache,
} from '../utils/holidayCalendar';

const HolidayContext = createContext(null);

export function HolidayProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshHolidays = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiGet('/api/holidays');
      if (!result?.success || !Array.isArray(result.data)) {
        throw new Error(result?.error || 'Failed to load holidays');
      }
      setHolidays(result.data);
      setHolidaysCache(result.data);
      return result.data;
    } catch (err) {
      console.error('HolidayContext: failed to load holidays', err);
      setError(err.message || 'Failed to load holidays');
      setHolidays([]);
      setHolidaysCache([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setHolidays([]);
      setHolidaysCache([]);
      return;
    }
    refreshHolidays();
  }, [authLoading, isAuthenticated, refreshHolidays]);

  const value = useMemo(
    () => ({
      holidays,
      isLoading,
      error,
      refreshHolidays,
      getHolidayByDate: lookupHolidayByDate,
    }),
    [holidays, isLoading, error, refreshHolidays]
  );

  return (
    <HolidayContext.Provider value={value}>{children}</HolidayContext.Provider>
  );
}

/** Stable identity so consumers rendered without a provider don't re-run effects each render. */
const FALLBACK_CONTEXT = Object.freeze({
  holidays: Object.freeze([]),
  isLoading: false,
  error: null,
  refreshHolidays: async () => [],
  getHolidayByDate: lookupHolidayByDate,
});

export function useHolidays() {
  const ctx = useContext(HolidayContext);
  if (!ctx) {
    return FALLBACK_CONTEXT;
  }
  return ctx;
}
